import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wheat, LifeBuoy, ArrowLeft } from "lucide-react";
import TicketCard from "@/components/TicketCard";
import Footer from "@/components/Footer";
import { ROLE_LABELS } from "@/lib/utils2";

export default function PublicHelpDesk() {
  const [lookup, setLookup] = useState({ case_number: "", phone: "" });
  const [found, setFound] = useState(null);
  const [ticket, setTicket] = useState({ phone: "", subject: "", message: "", ticket_type: "general", first_name: "", last_name: "", store_number: "", requested_role: "cafe_employee" });
  const [created, setCreated] = useState(null);
  const [loading, setLoading] = useState(false);

  const doLookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/tickets/lookup", lookup);
      setFound(data.ticket);
    } catch (err) {
      setFound(null);
      toast.error(errMsg(err));
    } finally { setLoading(false); }
  };

  const submitTicket = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/tickets", ticket);
      setCreated(data.ticket);
      toast.success(`Ticket created: ${data.ticket.case_number}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setLoading(false); }
  };

  const set = (k) => (e) => setTicket({ ...ticket, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-brand-page">
      <header className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-brand flex items-center justify-center"><Wheat className="w-5 h-5 text-white" /></div>
          <span className="font-display text-2xl text-brand-ink">The Spread</span>
        </Link>
        <Link to="/"><Button variant="ghost" className="text-brand-ink"><ArrowLeft className="w-4 h-4 mr-1.5" /> Home</Button></Link>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <LifeBuoy className="w-6 h-6 text-brand" />
          <h1 className="font-display text-4xl tracking-tight text-brand-ink">Help Desk</h1>
        </div>
        <p className="text-brand-muted mb-8">Check the status of an existing ticket or submit a new request — no login required.</p>

        <Tabs defaultValue="lookup">
          <TabsList className="bg-brand-sidebar h-auto p-1 rounded-full">
            <TabsTrigger value="lookup" data-testid="tab-lookup" className="rounded-full data-[state=active]:bg-white px-5 py-2">Check status</TabsTrigger>
            <TabsTrigger value="submit" data-testid="tab-submit" className="rounded-full data-[state=active]:bg-white px-5 py-2">Submit ticket</TabsTrigger>
          </TabsList>

          <TabsContent value="lookup" className="mt-6">
            <form onSubmit={doLookup} className="rounded-2xl border border-brand-line bg-white p-6 card-shadow space-y-4">
              <div>
                <Label>Case number</Label>
                <Input data-testid="lookup-case" value={lookup.case_number} onChange={(e) => setLookup({ ...lookup, case_number: e.target.value })} placeholder="SPD-123456" className="mt-1.5 border-brand-line" />
              </div>
              <div>
                <Label>Phone number on the ticket</Label>
                <Input data-testid="lookup-phone" value={lookup.phone} onChange={(e) => setLookup({ ...lookup, phone: e.target.value })} placeholder="10-digit phone" className="mt-1.5 border-brand-line" />
              </div>
              <Button type="submit" data-testid="lookup-submit" disabled={loading} className="rounded-full brand-btn">Look up ticket</Button>
            </form>
            {found && <div className="mt-5"><TicketCard ticket={found} /></div>}
          </TabsContent>

          <TabsContent value="submit" className="mt-6">
            {created ? (
              <div className="rounded-2xl border border-brand-line bg-white p-6 card-shadow" data-testid="ticket-created">
                <p className="text-sm text-brand-muted">Your case number (save this):</p>
                <p className="font-display text-3xl text-brand mt-1">{created.case_number}</p>
                <p className="text-brand-muted mt-3">Use this case number and your phone number anytime to check status. An admin will review your request.</p>
                <Button onClick={() => setCreated(null)} variant="outline" className="mt-4 rounded-md border-brand-line">Submit another</Button>
              </div>
            ) : (
              <form onSubmit={submitTicket} className="rounded-2xl border border-brand-line bg-white p-6 card-shadow space-y-4">
                <div>
                  <Label>Request type</Label>
                  <Select value={ticket.ticket_type} onValueChange={(v) => setTicket({ ...ticket, ticket_type: v })}>
                    <SelectTrigger data-testid="ticket-type" className="mt-1.5 border-brand-line"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General support</SelectItem>
                      <SelectItem value="cafe_registration">Unregistered cafe — create my account</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Phone number</Label>
                  <Input data-testid="ticket-phone" value={ticket.phone} onChange={set("phone")} placeholder="10-digit phone" className="mt-1.5 border-brand-line" />
                </div>
                {ticket.ticket_type === "cafe_registration" && (
                  <div className="grid sm:grid-cols-2 gap-3 rounded-xl bg-brand-sidebar/60 p-4 border border-brand-line">
                    <div><Label>First name</Label><Input data-testid="ticket-first" value={ticket.first_name} onChange={set("first_name")} className="mt-1.5 border-brand-line bg-white" /></div>
                    <div><Label>Last name</Label><Input data-testid="ticket-last" value={ticket.last_name} onChange={set("last_name")} className="mt-1.5 border-brand-line bg-white" /></div>
                    <div><Label>Store number</Label><Input data-testid="ticket-store" value={ticket.store_number} onChange={set("store_number")} className="mt-1.5 border-brand-line bg-white" /></div>
                    <div>
                      <Label>Requested role</Label>
                      <Select value={ticket.requested_role} onValueChange={(v) => setTicket({ ...ticket, requested_role: v })}>
                        <SelectTrigger data-testid="ticket-role" className="mt-1.5 border-brand-line bg-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <div>
                  <Label>Subject</Label>
                  <Input data-testid="ticket-subject" value={ticket.subject} onChange={set("subject")} className="mt-1.5 border-brand-line" />
                </div>
                <div>
                  <Label>Message</Label>
                  <Textarea data-testid="ticket-message" value={ticket.message} onChange={set("message")} rows={4} className="mt-1.5 border-brand-line" />
                </div>
                <Button type="submit" data-testid="ticket-submit" disabled={loading} className="rounded-full brand-btn">Submit ticket</Button>
              </form>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}
